// Solutions catalog — single source of truth for /solutions index and detail pages.
// Update sitemap.xml, llms.txt, and .qcoda/content.md in the same commit as any slug change.

export const SOLUTIONS = [
    {
        slug: 'software-delivery',
        eyebrow: 'Solution 01',
        title: 'Governed AI software delivery',
        tagline: 'AI-assisted engineering with the guardrails regulated teams require.',
        summary:
            'Turn plain-text work items into validated specs, ledger every decision, and keep AI-generated code aligned with business intent — inside your perimeter, on your models.',
        problem:
            'AI coding tools made writing code fast. They did not make alignment, review, or auditability easier. Feature requests get misinterpreted. Specs drift. Decisions live in Slack. When something ships wrong, no one can trace why — and in a regulated environment, that is not a minor problem.',
        approach: [
            'Enrich raw work items into validated developer specs contextualized against your codebase.',
            'Ledger every decision — date, rationale, alternatives considered, resolution of concerns.',
            'Enforce a drift gate before PR merge — if generated code strays from the spec, it blocks.',
            'Run inference on your infrastructure, your models. No forced third-party AI dependency.',
        ],
        outcomes: [
            'Provable alignment between product intent and deployed code.',
            'Institutional memory that survives team turnover.',
            'Audit-ready artifacts without extra ceremony.',
            'AI velocity without the trust tax.',
        ],
        alignmentThread:
            'Product owns what and why. Engineering owns how. Compliance sees the ledger. Auditors get artifacts, not tribal knowledge.',
        cta: 'See how it maps to your SDLC',
    },
    {
        slug: 'architecture-acceleration',
        eyebrow: 'Solution 02',
        title: 'Architecture acceleration',
        tagline: 'The incubator-model architecture partner for teams that need to move now.',
        summary:
            'Compress weeks of architecture, discovery, and technical planning into days — with governance baked in from the first decision, not bolted on after.',
        problem:
            'Greenfield programs stall on architecture debates. Migrations stall on unknowns. Consultants deliver decks; they do not deliver a decision ledger you can defend to a board or a regulator six months later.',
        approach: [
            'Accelerated architecture workshops driven by Qcoda-generated context and options.',
            'Every architectural choice enters the ledger with rationale, alternatives, and constraints.',
            'Outputs are executable specs — not slideware — that your delivery team can start against.',
            'Handoff includes the governance surface, not just the diagrams.',
        ],
        outcomes: [
            'Weeks-to-days compression on architecture and planning cycles.',
            'A defensible decision record from day one.',
            'Delivery teams start with validated specs, not opinions.',
        ],
        alignmentThread:
            'Executives get the strategic story. LOB owners get the constraints they set honored. Engineers get specs that hold up.',
        cta: 'Talk about a design engagement',
    },
    {
        slug: 'audit-compliance',
        eyebrow: 'Solution 03',
        title: 'AI-assisted audit & compliance review',
        tagline: 'Legal, software, and process audits — accelerated, evidenced, aligned.',
        summary:
            'Apply governed AI orchestration to the audit surface itself — legal reviews, software due diligence, control testing — with a ledgered evidence trail every stakeholder can trust.',
        problem:
            'Audit work is slow, expensive, and mostly manual. Where AI is applied, it is applied without provenance — a summary with no trail. Regulators, boards, and counterparties do not accept "the model said so."',
        approach: [
            'Structured intake of source material into a governed workspace.',
            'AI-generated findings that cite the underlying evidence at every step.',
            'Reviewer-in-the-loop gates keep humans accountable for every published finding.',
            'A ledger of every question asked, every model used, every human sign-off.',
        ],
        outcomes: [
            'Faster review cycles without abandoning provenance.',
            'Evidence packages that survive external scrutiny.',
            'Consistent methodology across engagements.',
        ],
        alignmentThread:
            'Partners set the standard. Associates and analysts do the work with guardrails. Clients get a defensible artifact — not a black box.',
        cta: 'Scope an audit engagement',
    },
    {
        slug: 'research-planning',
        eyebrow: 'Solution 04',
        title: 'Governed research & campaign planning',
        tagline: 'Strategic research, PR campaigns, and executive briefings — with the receipts.',
        summary:
            'Marketing, comms, and strategy teams increasingly ship AI-assisted work. Qcoda gives them the same alignment layer engineering gets — validated inputs, ledgered decisions, cited outputs.',
        problem:
            'AI in comms and strategy work has an accountability problem. Drafts get generated in a chat window and pasted into a plan. No one can reconstruct what was asked, what was rejected, why the messaging changed.',
        approach: [
            'Structured brief intake against a defined audience, objective, and constraints.',
            'AI-assisted research passes with cited sources and versioned outputs.',
            'Ledgered stakeholder decisions — every messaging shift traceable.',
            'Final deliverables ship with a provenance appendix, not just an executive summary.',
        ],
        outcomes: [
            'Campaigns and briefs that survive stakeholder review.',
            'Reusable institutional research rather than one-shot documents.',
            'A clean chain of custody from raw source to public statement.',
        ],
        alignmentThread:
            'Leadership sets objective and voice. LOB owners contribute inputs. Practitioners execute inside the guardrails. Nothing goes out unverified.',
        cta: 'Talk about a research engagement',
    },
];

export const INDUSTRIES = [
    { slug: 'fintech', name: 'Fintech' },
    { slug: 'insuretech', name: 'Insuretech' },
    { slug: 'legal', name: 'Legal' },
    { slug: 'hr', name: 'HR' },
    { slug: 'medical', name: 'Medical' },
    { slug: 'public-sector', name: 'Public sector' },
    { slug: 'higher-ed', name: 'Higher education' },
];

export function getSolution(slug) {
    return SOLUTIONS.find((s) => s.slug === slug) || null;
}
